/**
 * Per-product unboxing reels.
 *
 * Three jobs: load the sources late, play only what is on screen, and open a
 * tapped tile full screen with sound.
 *
 * Nothing is fetched until the section is within 300px of the viewport, and
 * even then only the tiles that come into view are played. On a phone at most
 * two run at once -- a row of five autoplaying reels is what makes this kind of
 * section expensive on a product page.
 */
(function () {
  'use strict';

  var NEAR = '300px';
  var MOBILE = 750;
  var MAX_PLAYING_MOBILE = 2;

  function prefersStill() {
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    var saveData = Boolean(conn && conn.saveData);
    return reduced || saveData;
  }

  function isMobile() {
    return window.innerWidth < MOBILE;
  }

  class CitronUnboxing extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ctr-unbox-track]');
      if (!this.track) return;

      this.tiles = Array.from(this.querySelectorAll('[data-ctr-unbox-tile]'));
      this.videos = this.tiles.map(function (t) {
        return t.querySelector('[data-ctr-unbox-video]');
      });
      this.still = prefersStill();
      this.wantsAutoplay = this.dataset.autoplay === 'true' && !this.still;
      this.loaded = false;
      this.visible = new Map();

      if (this.still) {
        this.tiles.forEach(function (t) {
          t.setAttribute('data-poster-only', '');
        });
      }

      this.watchApproach();
      this.wireTiles();
      this.wireArrows();
      this.wireDialog();
    }

    disconnectedCallback() {
      if (this.nearIo) this.nearIo.disconnect();
      if (this.playIo) this.playIo.disconnect();
      if (this.onKey) document.removeEventListener('keydown', this.onKey);
    }

    /* ------------------------------------------------------- lazy sources */

    watchApproach() {
      if (!('IntersectionObserver' in window)) {
        this.loadSources();
        return;
      }
      this.nearIo = new IntersectionObserver(
        function (entries) {
          if (!entries.some(function (e) { return e.isIntersecting; })) return;
          this.nearIo.disconnect();
          this.nearIo = null;
          // Reduced motion or Data Saver: nothing is going to play on its own,
          // so nothing is fetched either. The poster stands until a tile is
          // tapped, and then only that one video loads.
          if (!this.still) this.loadSources();
          this.watchPlayback();
        }.bind(this),
        { rootMargin: NEAR + ' 0px' }
      );
      this.nearIo.observe(this);
    }

    /** @param {number} [only] load just this tile's sources */
    loadSources(only) {
      if (this.loaded && only == null) return;
      if (only == null) this.loaded = true;
      this.videos.forEach(function (v, i) {
        if (!v) return;
        if (only != null && i !== only) return;
        var moved = false;
        v.querySelectorAll('source[data-src]').forEach(function (s) {
          s.src = s.dataset.src;
          s.removeAttribute('data-src');
          moved = true;
        });
        if (moved) {
          v.preload = 'metadata';
          v.load();
        }
      });
    }

    /* --------------------------------------------------- play what is seen */

    watchPlayback() {
      if (!this.wantsAutoplay || !('IntersectionObserver' in window)) return;

      this.playIo = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var i = this.tiles.indexOf(entry.target);
            if (i < 0) return;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              this.visible.set(i, entry.intersectionRatio);
            } else {
              this.visible.delete(i);
            }
          }, this);
          this.sync();
        }.bind(this),
        { threshold: [0, 0.5, 0.75, 1] }
      );
      this.tiles.forEach(function (t) {
        this.playIo.observe(t);
      }, this);
    }

    sync() {
      // On a phone only the two most-visible tiles run; on desktop everything
      // that is at least half on screen.
      var allowed;
      if (isMobile()) {
        allowed = Array.from(this.visible.entries())
          .sort(function (a, b) { return b[1] - a[1]; })
          .slice(0, MAX_PLAYING_MOBILE)
          .map(function (e) { return e[0]; });
      } else {
        allowed = Array.from(this.visible.keys());
      }

      this.videos.forEach(function (v, i) {
        if (!v) return;
        if (allowed.indexOf(i) > -1) {
          if (v.paused) {
            var r = v.play();
            if (r && r.catch) r.catch(function () { /* autoplay refused; the poster stands */ });
          }
        } else if (!v.paused) {
          v.pause();
        }
      });
    }

    /* ------------------------------------------------------------- arrows */

    wireArrows() {
      this.prev = this.querySelector('[data-ctr-unbox-prev]');
      this.next = this.querySelector('[data-ctr-unbox-next]');
      if (!this.prev && !this.next) return;

      var step = function () {
        var cell = this.querySelector('.ctr-unbox__cell');
        var w = cell ? cell.getBoundingClientRect().width : 220;
        return Math.max(w + 20, this.track.clientWidth * 0.8);
      }.bind(this);

      if (this.prev) {
        this.prev.addEventListener('click', function () {
          this.track.scrollBy({ left: -step(), behavior: 'smooth' });
        }.bind(this));
      }
      if (this.next) {
        this.next.addEventListener('click', function () {
          this.track.scrollBy({ left: step(), behavior: 'smooth' });
        }.bind(this));
      }

      var update = function () {
        var max = this.track.scrollWidth - this.track.clientWidth;
        if (this.prev) this.prev.hidden = this.track.scrollLeft <= 2;
        if (this.next) this.next.hidden = this.track.scrollLeft >= max - 2;
      }.bind(this);

      this.track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    }

    /* ----------------------------------------------------------- lightbox */

    wireTiles() {
      this.tiles.forEach(function (tile, i) {
        tile.addEventListener('click', function () {
          this.open(i);
        }.bind(this));
      }, this);
    }

    wireDialog() {
      this.dialog = this.querySelector('[data-ctr-unbox-dialog]');
      if (!this.dialog) return;
      this.player = this.dialog.querySelector('[data-ctr-unbox-player]');
      this.dprev = this.dialog.querySelector('[data-ctr-unbox-dprev]');
      this.dnext = this.dialog.querySelector('[data-ctr-unbox-dnext]');
      this.current = -1;

      var close = this.dialog.querySelector('[data-ctr-unbox-close]');
      if (close) close.addEventListener('click', this.close.bind(this));

      // The dialog fills the viewport, so "outside the card" means outside the
      // player itself rather than outside the dialog box.
      this.dialog.addEventListener('click', function (event) {
        if (event.target === this.dialog) this.close();
      }.bind(this));

      this.dialog.addEventListener('cancel', function (event) {
        event.preventDefault();
        this.close();
      }.bind(this));

      if (this.dprev) this.dprev.addEventListener('click', this.step.bind(this, -1));
      if (this.dnext) this.dnext.addEventListener('click', this.step.bind(this, 1));

      this.onKey = function (event) {
        if (!this.dialog.open) return;
        if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
      }.bind(this);
      document.addEventListener('keydown', this.onKey);

      this.wireSwipe();
    }

    wireSwipe() {
      var x0 = null;
      var y0 = null;
      this.dialog.addEventListener('touchstart', function (e) {
        var t = e.changedTouches[0];
        x0 = t.clientX;
        y0 = t.clientY;
      }, { passive: true });
      this.dialog.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var t = e.changedTouches[0];
        var dx = t.clientX - x0;
        var dy = t.clientY - y0;
        x0 = null;
        // Horizontal, and clearly so -- a vertical drag is a scroll gesture.
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.step(dx < 0 ? 1 : -1);
        }
      }.bind(this), { passive: true });
    }

    open(index) {
      if (!this.dialog || !this.player) return;
      this.loadSources(this.still ? index : undefined);
      this.opener = this.tiles[index] || document.activeElement;
      this.point(index);
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      var r = this.player.play();
      if (r && r.catch) r.catch(function () {});
    }

    point(index) {
      var source = this.videos[index];
      if (!source) return;
      this.current = index;
      this.player.pause();
      this.player.innerHTML = '';
      Array.from(source.querySelectorAll('source')).forEach(function (s) {
        var c = document.createElement('source');
        c.src = s.src || s.dataset.src || '';
        c.type = s.type;
        this.player.appendChild(c);
      }, this);
      if (source.poster) this.player.poster = source.poster;
      this.player.muted = false;
      this.player.load();

      var many = this.tiles.length > 1;
      if (this.dprev) this.dprev.hidden = !many;
      if (this.dnext) this.dnext.hidden = !many;
    }

    step(delta) {
      if (this.current < 0 || this.tiles.length < 2) return;
      var n = this.tiles.length;
      var next = (this.current + delta + n) % n;
      if (this.still) this.loadSources(next);
      this.point(next);
      var r = this.player.play();
      if (r && r.catch) r.catch(function () {});
    }

    close() {
      if (!this.dialog || !this.dialog.open) return;
      this.player.pause();
      this.dialog.close();
      document.documentElement.style.overflow = '';
      if (this.opener && this.opener.focus) this.opener.focus();
      this.opener = null;
      // The grid tiles carried on muted underneath; make sure the one that was
      // opened is in the right state for where the page now is.
      this.sync();
    }
  }

  if (!customElements.get('citron-unboxing')) {
    customElements.define('citron-unboxing', CitronUnboxing);
  }
})();
